import React, { useState, useEffect } from 'react';
import {
  Table, Button, Card, Space, Typography, Tag, Input, Modal,
  message, Popconfirm, Select, Upload, Image, Alert
} from 'antd';
import {
  SearchOutlined, PlusOutlined, EditOutlined, DeleteOutlined,
  PictureOutlined, UploadOutlined, DownloadOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../utils/auth';
import productService from '../services/productService';

const { Title, Text } = Typography;
const { Option } = Select;

const Products = () => {
  const navigate = useNavigate();
  const { tenant } = useAuth();
  const tenantId = tenant?.id;

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [categories, setCategories] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1, pageSize: 20, total: 0,
    showSizeChanger: true, showQuickJumper: true,
    showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
  });

  // Import modal states
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const fetchProducts = async (page = 1, pageSize = 20) => {
    if (!tenantId) return;
    try {
      setLoading(true);
      const params = { tenantId, page: page - 1, size: pageSize };
      if (searchText) params.keyword = searchText;
      if (categoryFilter) params.category = categoryFilter;
      const response = await productService.getProducts(params);
      if (response.success) {
        setProducts(response.data.content || []);
        setPagination(prev => ({ ...prev, current: page, pageSize, total: response.data.totalElements || 0 }));
      }
    } catch (error) {
      message.error('获取商品列表失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    if (!tenantId) return;
    try {
      const response = await productService.getCategories(tenantId);
      if (response.success) setCategories(response.data || []);
    } catch (error) { /* ignore */ }
  };

  useEffect(() => { fetchProducts(); fetchCategories(); }, [tenantId]);

  const handleSearch = (value) => { setSearchText(value); fetchProducts(1, pagination.pageSize); };
  const handleTableChange = (pag) => { fetchProducts(pag.current, pag.pageSize); };

  const handleDelete = async (id) => {
    try {
      const response = await productService.deleteProduct(id, tenantId);
      if (response.success) { message.success('删除成功'); fetchProducts(pagination.current, pagination.pageSize); }
      else message.error(response.message || '删除失败');
    } catch (error) { message.error('删除失败'); }
  };

  const handleImport = async (file) => {
    try {
      setImporting(true);
      setImportResult(null);
      const response = await productService.importProducts(tenantId, file);
      if (response.success) {
        setImportResult(response.data);
        if (response.data.successCount > 0) {
          message.success(response.message);
          fetchProducts();
          fetchCategories();
        }
      } else {
        message.error(response.message || '导入失败');
      }
    } catch (error) {
      message.error('导入失败');
    } finally {
      setImporting(false);
    }
    return false;
  };

  const columns = [
    {
      title: '商品图片', dataIndex: 'images', key: 'images', width: 80,
      render: (images) => {
        const firstImage = images && images.length > 0 ? images[0] : null;
        return firstImage ? (
          <Image src={firstImage.ossUrl || firstImage.url} width={50} height={50} style={{ objectFit: 'cover', borderRadius: 4 }} />
        ) : (
          <div style={{ width: 50, height: 50, background: '#f5f5f5', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <PictureOutlined style={{ color: '#ccc' }} />
          </div>
        );
      }
    },
    { title: '商品名称', dataIndex: 'name', key: 'name', ellipsis: true },
    { title: '分类', dataIndex: 'category', key: 'category', width: 100, render: (v) => v ? <Tag>{v}</Tag> : '-' },
    { title: '价格', dataIndex: 'price', key: 'price', width: 100, render: (v) => v ? `¥${v}` : '-' },
    {
      title: '图片数', key: 'imageCount', width: 80,
      render: (_, record) => (record.images || []).length
    },
    {
      title: '状态', dataIndex: 'status', key: 'status', width: 80,
      render: (status) => <Tag color={status === 'ACTIVE' ? 'green' : 'default'}>{status === 'ACTIVE' ? '上架' : '下架'}</Tag>
    },
    {
      title: '操作', key: 'action', width: 150,
      render: (_, record) => (
        <Space>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => navigate(`/products/edit/${record.id}`)}>编辑</Button>
          <Popconfirm title="确定删除该商品？" onConfirm={() => handleDelete(record.id)} okText="确定" cancelText="取消">
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      )
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={5} style={{ margin: 0 }}>商品管理</Title>
        <Space>
          <Button icon={<UploadOutlined />} onClick={() => { setImportModalVisible(true); setImportResult(null); }}>
            批量导入
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/products/create')}>新增商品</Button>
        </Space>
      </div>
      <Card>
        <Space style={{ marginBottom: 16 }}>
          <Input.Search placeholder="搜索商品名称" allowClear onSearch={handleSearch} style={{ width: 250 }} prefix={<SearchOutlined />} />
          <Select placeholder="按分类筛选" allowClear style={{ width: 150 }} onChange={(v) => { setCategoryFilter(v); fetchProducts(1, pagination.pageSize); }}>
            {categories.map(c => <Option key={c} value={c}>{c}</Option>)}
          </Select>
        </Space>
        <Table columns={columns} dataSource={products} rowKey="id" loading={loading} pagination={pagination} onChange={handleTableChange} />
      </Card>

      {/* 批量导入弹窗 */}
      <Modal
        title="批量导入商品"
        open={importModalVisible}
        onCancel={() => setImportModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setImportModalVisible(false)}>关闭</Button>,
        ]}
        width={520}
      >
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <div>
            <Text strong>第一步：下载模板</Text>
            <div style={{ marginTop: 8 }}>
              <Button icon={<DownloadOutlined />} onClick={() => productService.downloadTemplate()}>
                下载 Excel 模板
              </Button>
            </div>
          </div>

          <div>
            <Text strong>第二步：上传文件</Text>
            <div style={{ marginTop: 8 }}>
              <Upload
                accept=".xlsx,.xls"
                showUploadList={false}
                beforeUpload={handleImport}
                disabled={importing}
              >
                <Button icon={<UploadOutlined />} loading={importing}>
                  {importing ? '导入中...' : '选择 Excel 文件上传'}
                </Button>
              </Upload>
            </div>
            <Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
              支持 .xlsx 格式，必填列：商品名称
            </Text>
          </div>

          {importResult && (
            <div>
              <Text strong>导入结果</Text>
              <div style={{ marginTop: 8 }}>
                <Alert
                  type={importResult.failCount === 0 ? 'success' : 'warning'}
                  message={`成功导入 ${importResult.successCount} 条，失败 ${importResult.failCount} 条`}
                  showIcon
                />
                {importResult.errors && importResult.errors.length > 0 && (
                  <div style={{ marginTop: 8, maxHeight: 200, overflow: 'auto' }}>
                    {importResult.errors.map((err, idx) => (
                      <div key={idx} style={{ fontSize: 12, color: '#ff4d4f', marginBottom: 2 }}>
                        第{err.row}行：{err.reason}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </Space>
      </Modal>
    </div>
  );
};

export default Products;
